import { TraceChip, useFormat } from '@ly360/ui';
import {
  AlertTriangle,
  ArrowDown,
  Check,
  CheckCheck,
  Clock,
  HelpCircle,
  Lock,
  Globe,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
} from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiBlob } from '../lib/api';
import { useAuth } from '../lib/auth';
import { MessageTranslation, useCanTranslate } from './Translate';
import type { CallInfo, ThreadItem } from './types';

const GROUP_GAP_MS = 5 * 60_000;
const LONG_TEXT = 700;

function DeliveryIcon({ m }: { m: ThreadItem }) {
  const { t } = useTranslation();
  if (!m.deliveryStatus) return null;
  const label = t(`inbox.delivery.${m.deliveryStatus}`, { error: m.deliveryError ?? '' });
  const icon = {
    queued: <Clock size={13} />,
    sending: <Clock size={13} />,
    sent: <Check size={13} />,
    delivered: <CheckCheck size={13} />,
    read: <CheckCheck size={13} className="read" />,
    failed: <AlertTriangle size={13} />,
    unknown: <HelpCircle size={13} />,
  }[m.deliveryStatus];
  return (
    <span className="delivery" title={label} aria-label={label} data-status={m.deliveryStatus}>
      {icon}
      {(m.deliveryStatus === 'failed' || m.deliveryStatus === 'unknown') && (
        <span className="delivery-text">{label}</span>
      )}
    </span>
  );
}

/** Long messages (typically emails) open collapsed. */
function MessageText({ text }: { text: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const long = (text?.length ?? 0) > LONG_TEXT;
  return (
    <>
      <div dir="auto" className="msg-text">
        {long && !open ? `${text!.slice(0, LONG_TEXT).trimEnd()}…` : text}
      </div>
      {long && (
        <button type="button" className="msg-more" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t('inbox.showLess') : t('inbox.showMore')}
        </button>
      )}
    </>
  );
}

type Side = 'in' | 'out' | 'note';
const sideOf = (m: ThreadItem): Side =>
  m.kind === 'note' ? 'note' : m.direction === 'inbound' ? 'in' : 'out';

interface Group {
  side: Side;
  author: string | null;
  items: ThreadItem[];
}

/**
 * The conversation: day separators, consecutive messages grouped by sender, time and delivery inside each bubble
 * (full date and zone on hover), internal notes inline, and a jump-to-latest button when scrolled up.
 */
/** Group key for messages written by the AI assistant (they have no staff author). */
const ASSISTANT = '\u0000assistant';

export function MessageList({ items, customerName }: { items: ThreadItem[]; customerName: string }) {
  const canTranslate = useCanTranslate();
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can } = useAuth();
  const showTrace = can('ops:dlq');
  const scroller = useRef<HTMLDivElement>(null);
  const [unseen, setUnseen] = useState(0);
  const lastCount = useRef(0);

  const dayKey = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: fmt.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso));
  const today = dayKey(new Date().toISOString());
  const yesterday = dayKey(new Date(Date.now() - 86_400_000).toISOString());
  const dayLabel = (key: string, iso: string) =>
    key === today ? t('inbox.day.today') : key === yesterday ? t('inbox.day.yesterday') : fmt.date(iso);

  // Group by day, then by consecutive sender within a few minutes.
  const days: { key: string; iso: string; groups: Group[] }[] = [];
  for (const m of items) {
    const key = dayKey(m.sentAt);
    let day = days.at(-1);
    if (!day || day.key !== key) days.push((day = { key, iso: m.sentAt, groups: [] }));
    const side = sideOf(m);
    const author = side === 'in' ? null : m.byAssistant ? ASSISTANT : m.authorName;
    const g = day.groups.at(-1);
    const prev = g?.items.at(-1);
    if (
      g &&
      prev &&
      g.side === side &&
      g.author === author &&
      side !== 'note' &&
      new Date(m.sentAt).getTime() - new Date(prev.sentAt).getTime() < GROUP_GAP_MS
    )
      g.items.push(m);
    else day.groups.push({ side, author, items: [m] });
  }

  const nearBottom = () => {
    const el = scroller.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 160;
  };
  const toBottom = (smooth = false) => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setUnseen(0);
  };

  // First render: start at the latest message. Later: follow new messages only if already near the bottom.
  useLayoutEffect(() => {
    const added = items.length - lastCount.current;
    if (lastCount.current === 0 || nearBottom()) toBottom();
    else if (added > 0) setUnseen((n) => n + added);
    lastCount.current = items.length;
  }, [items.length]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => nearBottom() && setUnseen(0);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const initials = customerName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="thread-scroll">
      <div className="thread-body" data-testid="thread" ref={scroller}>
        {days.map((day) => (
          <Fragment key={day.key}>
            <div className="day-sep" role="separator">
              <span>{dayLabel(day.key, day.iso)}</span>
            </div>
            {day.groups.map((g) =>
              g.side === 'note' ? (
                g.items.map((m) => (
                  <div key={m.id} className="note-bubble" data-kind="note">
                    <div className="note-meta">
                      <Lock size={12} aria-hidden="true" />
                      {t('inbox.internalNote')} · {m.authorName ?? t('assistant.name')}
                      <time dateTime={m.sentAt} title={`${fmt.dateTime(m.sentAt)} · ${fmt.zoneCaption}`}>
                        {fmt.time(m.sentAt)}
                      </time>
                    </div>
                    <MessageText text={m.text} />
                  </div>
                ))
              ) : (
                <div key={g.items[0]!.id} className={`msg-group ${g.side}`}>
                  {g.side === 'in' && (
                    <span className="msg-avatar" aria-hidden="true">
                      {initials}
                    </span>
                  )}
                  <div className="msg-stack">
                    {g.side === 'out' && g.author && (
                      <div className="msg-author">
                        {g.author === ASSISTANT ? (
                          <>
                            {t('assistant.name')} <span className="ai-pill">{t('assistant.ai')}</span>
                          </>
                        ) : (
                          g.author
                        )}
                      </div>
                    )}
                    {g.items.map((m) => (
                      <div
                        key={m.id}
                        className={`bubble ${g.side}`}
                        data-message-id={m.id}
                        data-delivery={m.deliveryStatus ?? undefined}
                      >
                        {m.replyMode && (
                          <div className="bubble-tag">{t(`inbox.replyMode.${m.replyMode}`)}</div>
                        )}
                        {m.templateName && (
                          <div className="bubble-tag">
                            {t('inbox.template')}: {m.templateName}
                          </div>
                        )}
                        {m.call ? <CallCard messageId={m.id} call={m.call} /> : <MessageText text={m.text} />}
                        {canTranslate && !m.call && m.text && (
                          <MessageTranslation messageId={m.id} text={m.text} />
                        )}
                        <div className="bubble-meta">
                          {showTrace && <TraceChip traceId={m.traceId} />}
                          <time dateTime={m.sentAt} title={`${fmt.dateTime(m.sentAt)} · ${fmt.zoneCaption}`}>
                            {fmt.time(m.sentAt)}
                          </time>
                          <DeliveryIcon m={m} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </Fragment>
        ))}
      </div>
      {unseen > 0 && (
        <button
          type="button"
          className="jump-latest"
          onClick={() => toBottom(true)}
          data-testid="jump-latest"
        >
          <ArrowDown size={14} aria-hidden="true" />
          {t('inbox.newMessages', { count: unseen })}
        </button>
      )}
    </div>
  );
}

/** A phone call in the timeline: who called, the outcome, the recording and the transcript. */
function CallCard({ messageId, call }: { messageId: string; call: CallInfo }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const minutes = Math.floor(call.durationSec / 60);
  const seconds = String(call.durationSec % 60).padStart(2, '0');
  const play = async () => {
    // Recordings are fetched with the user's session (they are not public), then played locally.
    const blob = await apiBlob(`/v1/calls/${messageId}/recording`).catch(() => null);
    if (blob) setSrc(URL.createObjectURL(blob));
  };
  return (
    <div className="call-card" data-testid="call-card">
      <div className="call-head">
        {call.direction === 'inbound' ? (
          <PhoneIncoming size={15} aria-hidden="true" />
        ) : call.direction === 'web' ? (
          <Globe size={15} aria-hidden="true" />
        ) : (
          <PhoneOutgoing size={15} aria-hidden="true" />
        )}
        <strong>{t(`inbox.call.${call.direction}`)}</strong>
        <span className={`badge ${call.status === 'answered' ? 'badge-neutral' : 'badge-warning'}`}>
          {t(`inbox.call.status.${call.status}`)}
        </span>
        {call.status === 'answered' && (
          <span className="faint num">{`${fmt.number(minutes)}:${seconds}`}</span>
        )}
        {call.handledBy !== 'none' && call.botName && (
          <span className="ai-pill" title={call.botName}>
            {t('assistant.ai')}
          </span>
        )}
      </div>
      {call.analysis?.summary && (
        <p className="call-summary" dir="auto" data-testid="call-summary">
          {call.analysis.summary}
        </p>
      )}
      {(call.analysis?.outcome ||
        call.analysis?.sentiment ||
        call.transfer ||
        call.transferTo ||
        call.web) && (
        <div className="call-meta">
          {call.analysis?.outcome && (
            <span className="badge badge-neutral">
              {t(`inbox.call.outcome.${call.analysis.outcome}`, { defaultValue: call.analysis.outcome })}
            </span>
          )}
          {call.analysis?.sentiment && (
            <span
              className={`badge ${call.analysis.sentiment.label === 'negative' ? 'badge-warning' : 'badge-neutral'}`}
            >
              {t(`inbox.call.sentiment.${call.analysis.sentiment.label}`, {
                defaultValue: call.analysis.sentiment.label,
              })}
            </span>
          )}
          {(call.transfer?.to ?? call.transferTo) && (
            <span className="faint">
              <PhoneForwarded size={13} aria-hidden="true" />{' '}
              {t('inbox.call.transferred', { to: call.transfer?.to ?? call.transferTo })}
            </span>
          )}
          {call.web && (
            <span className="faint mono" dir="ltr">
              {call.web.origin}
              {call.web.page}
            </span>
          )}
        </div>
      )}
      {call.collected && Object.keys(call.collected).length > 0 && (
        <dl className="kv call-collected" data-testid="call-collected">
          {Object.entries(call.collected).map(([k, v]) => (
            <Fragment key={k}>
              <dt>{t(`inbox.call.collectedKeys.${k}`, { defaultValue: k })}</dt>
              <dd dir="auto">{v}</dd>
            </Fragment>
          ))}
        </dl>
      )}
      {call.recording &&
        (src ? (
          <audio controls src={src} className="call-audio" />
        ) : (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void play()}
            data-testid="play-recording"
          >
            <Play size={13} />
            {t('inbox.call.play')}
          </button>
        ))}
      {call.transcript.length > 0 && (
        <>
          <button
            type="button"
            className="link-btn"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-testid="toggle-transcript"
          >
            {open
              ? t('inbox.call.hideTranscript')
              : t('inbox.call.showTranscript', { count: call.transcript.length })}
          </button>
          {open && (
            <ol className="list-plain call-transcript">
              {call.transcript.map((s, i) => (
                <li key={i} className={`speaker-${s.speaker}`}>
                  <span className="faint">{t(`inbox.call.speaker.${s.speaker}`)}</span>
                  <span dir="auto">{s.text}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
